/**
 * Usage, the workspace's (Figma 09 · Workspace usage 01–10), Owner only. Not
 * built, no backend yet: top-up requests, Top up / Not now, Request upgrade
 * (top-ups are given by a platform admin in the CMS).
 */
import { ArrowRight } from "lucide-react";
import { budgetCeilingUsd, budgetUsedPct } from "@/hooks/useAiBudget";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useOrganizationUsage, type MemberUsage } from "@/hooks/useOrganizationUsage";
import { useOrganizationMembers } from "@/app/routes/dashboard/settings/api/useOrganization";
import { ErrorState } from "../../ViewStates";
import {
  Card,
  CardLoading,
  GroupLabel,
  Meter,
  Note,
  PageHead,
  PersonMark,
  PersonMeter,
  QuietNote,
  SectionHead,
  SettingsPage,
  TextAction,
} from "../parts/SettingsParts";
import { useWorkspaceHead } from "../parts/scope";
import { roleLabel } from "../parts/roles";
import { formatResetDay, formatUsd, listJoin, monthName, plural } from "../parts/settingsFormat";

const DAY_MS = 86_400_000;

/** "Day 22 of 31: at this pace October ends at about $195." Null early in the month. */
function paceLine(spent: number, startIso: string, endIso: string, month: string): string | null {
  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();
  const now = Date.now();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
  const days = Math.round((end - start) / DAY_MS);
  const day = Math.min(days, Math.max(1, Math.ceil((now - start) / DAY_MS)));
  if (day < 3 || spent <= 0) return null;
  const projected = (spent / ((now - start) / (end - start))) | 0;
  return `Day ${day} of ${days}: at this pace ${month} ends at about ${formatUsd(projected)}.`;
}

export default function WorkspaceUsagePage() {
  const head = useWorkspaceHead();
  const { user, selectedOrganization } = useCurrentUser();
  const { data, isLoading, isError, refetch } = useOrganizationUsage(true);
  const { data: members } = useOrganizationMembers(selectedOrganization?.organization.id ?? null);

  if (isLoading || isError || !data) {
    return (
      <SettingsPage>
        <PageHead {...head} title="Usage" status={isLoading ? "Reading the workspace’s usage." : undefined} />
        {isLoading ? (
          <CardLoading caption="Loading usage" rows={6} />
        ) : (
          <ErrorState
            title="Usage did not load"
            body="Creo could not read it. Nothing has changed, and nobody’s allowance is affected."
            onRetry={() => void refetch()}
          />
        )}
      </SettingsPage>
    );
  }

  const pool = data.organization;
  const month = monthName(pool.periodStart);
  const resets = formatResetDay(pool.periodEnd);
  const pct = budgetUsedPct(pool);
  const usedUp = pool.exhausted || pct >= 100;
  const brandsByUser = new Map((members ?? []).map((m) => [m.userId, m.brands ?? []]));

  const byUse = (a: MemberUsage, b: MemberUsage) => b.spentUsd - a.spentUsd;
  const team = data.members.filter((m) => m.role !== "Guest").sort(byUse);
  const clients = data.members.filter((m) => m.role === "Guest").sort(byUse);
  const outOf = data.members.filter((m) => m.exhausted).map((m) => m.name || m.email);

  const status = [
    `${month}. The workspace pool is ${usedUp ? "used up" : `at ${Math.round(pct)}%`}${
      !usedUp && pct < 80 && outOf.length === 0 ? ", and everyone has room" : ""
    }.`,
    outOf.length ? `${listJoin(outOf)} ${outOf.length === 1 ? "is" : "are"} out of allowance.` : null,
  ]
    .filter(Boolean)
    .join(" ");

  const pace = usedUp ? null : paceLine(pool.spentUsd, pool.periodStart, pool.periodEnd, month);

  const row = (m: MemberUsage) => {
    const isYou = m.userId === user?.id;
    const brands = brandsByUser.get(m.userId) ?? [];
    const meta =
      m.role === "Guest"
        ? `Client · ${brands.length === 1 ? brands[0].name : plural(brands.length, "brand")}`
        : `${roleLabel(m.role)}${isYou ? " · You" : ""}`;
    return (
      <PersonMeter
        key={m.userId}
        lead={<PersonMark name={m.name || m.email} size={32} />}
        name={m.name || m.email}
        meta={m.exhausted ? `${meta} · out of allowance` : meta}
        metaTone={m.exhausted ? "alert" : "muted"}
        amount={`${formatUsd(m.spentUsd)} of ${formatUsd(budgetCeilingUsd(m))}`}
        pct={budgetUsedPct(m)}
      />
    );
  };

  return (
    <SettingsPage>
      <PageHead {...head} title="Usage" status={status} />
      <Card>
        <Meter
          pct={pct}
          caption={`of the workspace pool used in ${month}`}
          left={`${formatUsd(pool.spentUsd)} of ${formatUsd(budgetCeilingUsd(pool))}`}
          right={`Resets ${resets}`}
        />
        {usedUp ? (
          <Note tone="alert">
            The pool is used up. Overnight planning has stopped until {resets}. Client reviews kept running
            {pool.overspendUsd > 0 ? `, so the pool is ${formatUsd(pool.overspendUsd)} over` : ""}.
          </Note>
        ) : pct >= 80 ? (
          <Note tone="alert">
            The pool is at {Math.round(pct)}%. At 100% overnight planning stops until {resets}. Client reviews keep
            running.
          </Note>
        ) : (
          <QuietNote>
            The pool pays for overnight planning, and for the work a client’s review sets off.{pace ? ` ${pace}` : ""}
          </QuietNote>
        )}
        {pct >= 80 ? (
          <TextAction icon={ArrowRight} to=".?page=plan">
            Plan and seats
          </TextAction>
        ) : null}

        <SectionHead title="People" meta={`${data.members.length} · most used first`} />
        <div className="-mx-3 flex flex-col gap-0.5">
          {team.length ? <GroupLabel>Team</GroupLabel> : null}
          {team.map(row)}
          {clients.length ? <GroupLabel>Clients</GroupLabel> : null}
          {clients.map(row)}
        </div>
        <QuietNote>Each person’s allowance resets on the 1st. Mohtawa gives top-ups for now.</QuietNote>
      </Card>
    </SettingsPage>
  );
}
